import { Else, If, Then, When } from 'react-if';
import { withAsyncBoundary } from '@toss/async-boundary';
import cns from 'classnames';
import { isNil } from 'lodash-es';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { match } from 'ts-pattern';
import { LabelStatus } from '@zep/types';
import { Colors } from '@zep/ui-tokens';
import { useGetBillingInvoice } from '../../../../services';
import { InvoiceDto, OperationType, PaymentStatus } from '../../../../types/SubscriptionType';
import { fCurrency, fDateByCountryCode } from '../../../../utils';
import { refineSaleType } from '../../shared/refineSaleType';
import { SubscriptionRejectedFallback } from '../components/SubscriptionRejectedFallback';
import { AdditionalLabel } from '../space/atoms/AdditionalLabel';
import { InvoicePageSkeleton } from '../space/skeletons/InvoicePageSkeleton';
import S from './Invoice.module.scss';
const InvoiceContent = () => {
  const router = useRouter();
  const {
    id
  } = router.query;
  const {
    t
  } = useTranslation(['subscription']);
  const {
    data
  } = useGetBillingInvoice(String(id));
  const {
    countryCode,
    approvedAt,
    startDate,
    endDate,
    currency,
    spaceName,
    spaceOwnerEmail,
    spaceUrl,
    orderId,
    finalPrice,
    planType,
    maxCcu,
    originalPrice,
    saleType,
    saleDiscountPrice,
    previousPlan,
    unusedPrice,
    paymentStatus,
    refundedAt,
    zipCode,
    onetimePeriod,
    salePrice,
    vatAmount
  } = data;
  const currencyString = t('subscription:currency.won');
  const getPaymentMethodString = (invoice: InvoiceDto): string => {
    if (invoice.operationType === OperationType.정기 || invoice.operationType === OperationType.커스텀_정기) {
      return invoice.cardName + (invoice.cardNumber ? ` (${invoice.cardNumber})` : '');
    }
    switch (invoice.paymentType) {
      case 'CARD':
        return `${t('subscription:Invoice.paymentInfo.PaymentMethodCard')} (${invoice.cardNumber})`;
      case 'TRANSFER':
        return t('subscription:Invoice.paymentInfo.PaymentMethodTransfer');
      case 'MANUAL_TRANSFER':
        return t('subscription:Invoice.paymentInfo.PaymentMethodManualTransfer');
      case 'VIRTUAL_ACCOUNT':
        return t('subscription:Invoice.paymentInfo.PaymentMethodVirtual');
      default:
        return '';
    }
  };
  return <div className={S.wrapper} data-sentry-component="InvoiceContent" data-sentry-source-file="Invoice.tsx">
      <div className={S.row}>
        <div className={S.col}>
          <div className={S.logo}>
            <img src="/images/light/layout/logo_zep.svg" alt="" width={110} />
            <h1 className={S.title}>{t('subscription:Invoice.title')}</h1>
          </div>
        </div>
        <div className={S.col}>
          <section className={S.address}>
            <div>
              <strong>{t('subscription:Invoice.companyInfo.name')}</strong>
            </div>
            <Link href="https://zep.us" target="_blank" prefetch={false} data-sentry-element="Link" data-sentry-source-file="Invoice.tsx">
              https://zep.us
            </Link>
            <div>{t('subscription:Invoice.companyInfo.registerNumber')}</div>
            <div>{t('subscription:Invoice.companyInfo.adress')}</div>
          </section>
        </div>
      </div>

      <div className={S.line} aria-hidden />

      <div className={S.row}>
        <div className={S.col}>
          <dl className={S.dl}>
            <dt>{t('subscription:Invoice.paymentInfo.space')}</dt>
            <dd>
              <div>{spaceName}</div>
              <small>
                <Link href={spaceUrl} target="_blank" prefetch={false} data-sentry-element="Link" data-sentry-source-file="Invoice.tsx">
                  {spaceUrl}
                </Link>
              </small>
            </dd>
          </dl>
          <dl className={S.dl}>
            <dt>{t('subscription:Invoice.paymentInfo.billedTo')}</dt>
            <dd>
              <div>{spaceOwnerEmail}</div>
              <small>{t('subscription:Invoice.paymentInfo.spaceOwner')}</small>
            </dd>
          </dl>
        </div>
        <div className={S.col}>
          <dl className={S.dl}>
            <dt>{t('subscription:Invoice.paymentInfo.invoiceData')}</dt>
            <dd>{fDateByCountryCode(approvedAt, countryCode)}</dd>
          </dl>
          <dl className={S.dl}>
            <dt>{t('subscription:Invoice.paymentInfo.orderNumber')}</dt>
            <dd>{orderId}</dd>
          </dl>
          <dl className={S.dl}>
            <dt>{t('subscription:Invoice.paymentInfo.PaymentMethod')}</dt>
            <dd>{getPaymentMethodString(data)}</dd>
          </dl>
          <When condition={!isNil(zipCode)} data-sentry-element="When" data-sentry-source-file="Invoice.tsx">
            <dl className={S.dl}>
              <dt>{t('subscription:invoice.zipCode.dtTitle')}</dt>
              <dd>{zipCode}</dd>
            </dl>
          </When>
        </div>
      </div>

      <div className={S.line} aria-hidden />

      <div className={cns(S.row, S.row_total)}>
        <strong>
          {fCurrency(finalPrice, currency, {
          currencyString
        })}
        </strong>
        <span>
          {t('subscription:Invoice.priceInfo.expiredDateInfo', {
          currentPlanExpiredDate: fDateByCountryCode(endDate, countryCode)
        })}
        </span>
        <When condition={paymentStatus === PaymentStatus.REFUNDED} data-sentry-element="When" data-sentry-source-file="Invoice.tsx">
          <AdditionalLabel status={LabelStatus.CANCELED} data-sentry-element="AdditionalLabel" data-sentry-source-file="Invoice.tsx" />
        </When>
      </div>

      <div>
        <table className={S.table}>
          <thead>
            <tr>
              <td>{t('subscription:Invoice.priceInfo.detailContent')}</td>
              <td className={S.price}>
                {t('subscription:Invoice.priceInfo.price')}
              </td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <strong>
                  {planType.capitalize()} -{' '}
                  {maxCcu < 0 ? t('subscription:pricing.calculation.barUnlimited') : maxCcu}{' '}
                  {t('subscription:Invoice.priceInfo.Users')}
                </strong>
                <div>
                  {fDateByCountryCode(startDate, countryCode)}
                  {' - '}
                  {fDateByCountryCode(endDate, countryCode)}
                </div>
              </td>
              <td className={S.price}>
                <strong>
                  {fCurrency(originalPrice, currency, {
                  currencyString
                })}
                </strong>
              </td>
            </tr>
            {/* children 을 함수로 넘겨야 saleType 이 없을 때 아래 t() 가 호출되지 않는다. */}
            <When condition={!!saleType} data-sentry-element="When" data-sentry-source-file="Invoice.tsx">
              {() => <tr>
                  <td>
                    <strong>
                      {t('subscription:Invoice.priceInfo.eventDiscount')}
                    </strong>
                    <div>
                      {t('subscription:Invoice.priceInfo.eventDiscountInfo', {
                    eventType: (t as Function)(`subscription:pricing.plansCommon.${refineSaleType(saleType)}`, {
                      maxCcu
                    })
                  })}
                    </div>
                  </td>
                  <td className={S.price}>
                    <strong>
                      -{' '}
                      {fCurrency(saleDiscountPrice, currency, {
                    currencyString
                  })}
                    </strong>
                  </td>
                </tr>}
            </When>
            <When condition={Boolean(previousPlan)} data-sentry-element="When" data-sentry-source-file="Invoice.tsx">
              <tr>
                <td>
                  <strong>
                    {t('subscription:Invoice.priceInfo.discountPrice')}
                  </strong>
                  <div>
                    {match(onetimePeriod).with(null, () => t('subscription:Invoice.priceInfo.discountInfo', {
                    previousPlan: previousPlan?.planType,
                    ccu: previousPlan?.maxCcu === -1 ? t('subscription:pricing.calculation.barUnlimited') : String(previousPlan?.maxCcu),
                    previousStartDate: fDateByCountryCode(previousPlan?.startDate ?? Date.now(), countryCode),
                    previousEndDate: fDateByCountryCode(previousPlan?.endDate ?? Date.now(), countryCode)
                  })).with('M', () => t('subscription:Invoice.priceInfo.discountInfoOntimeNmonth', {
                    nextPrice: fCurrency(salePrice, currency, {
                      position: 'pre'
                    }),
                    prevPrice: fCurrency(previousPlan?.salePrice ?? 0, currency, {
                      position: 'pre'
                    }),
                    tmpPrice: fCurrency(salePrice - (previousPlan?.salePrice ?? 0), currency, {
                      position: 'pre'
                    }),
                    unusedDays: previousPlan?.unusedDayCount,
                    salePrice: fCurrency(finalPrice, currency, {
                      position: 'pre'
                    })
                  })).with('D', () => t('subscription:Invoice.priceInfo.discountInfoOntime1day3day', {
                    nextPrice: fCurrency(salePrice, currency, {
                      position: 'pre'
                    }),
                    prevPrice: fCurrency(previousPlan?.salePrice ?? 0, currency, {
                      position: 'pre'
                    }),
                    salePrice: fCurrency(finalPrice, currency, {
                      position: 'pre'
                    })
                  })).exhaustive()}
                  </div>
                </td>
                <td className={S.price}>
                  <strong>
                    -{' '}
                    {fCurrency(onetimePeriod === 'D' ? previousPlan?.salePrice ?? 0 : unusedPrice, currency, {
                    currencyString
                  })}
                  </strong>
                </td>
              </tr>
            </When>

            <When condition={(vatAmount ?? 0) > 0} data-sentry-element="When" data-sentry-source-file="Invoice.tsx">
              <tr>
                <td>
                  <strong>{t('subscription:payment.summary.vat')}</strong>
                </td>
                <td className={S.price}>
                  <strong>
                    +{' '}
                    {fCurrency(vatAmount, currency, {
                    currencyString
                  })}
                  </strong>
                </td>
              </tr>
            </When>

            <When condition={paymentStatus === PaymentStatus.REFUNDED} data-sentry-element="When" data-sentry-source-file="Invoice.tsx">
              <tr>
                <td>
                  <strong style={{
                  color: Colors.RedDefault
                }}>
                    {t('subscription:pricing.alert.cancel')}
                  </strong>
                  <div>
                    {t('subscription:Invoice.priceInfo.hasCanceledAt', {
                    date: fDateByCountryCode(refundedAt, countryCode)
                  })}
                  </div>
                </td>
                <td className={S.price}>
                  <strong>
                    -{' '}
                    {fCurrency(finalPrice, currency, {
                    currencyString
                  })}
                  </strong>
                </td>
              </tr>
            </When>
          </tbody>
        </table>

        <dl className={S.total}>
          <dt>
            <If condition={(vatAmount ?? 0) > 0} data-sentry-element="If" data-sentry-source-file="Invoice.tsx">
              <Then data-sentry-element="Then" data-sentry-source-file="Invoice.tsx">
                {t('subscription:Invoice.priceInfo.totalAmountIncludedVat')}
              </Then>
              <Else data-sentry-element="Else" data-sentry-source-file="Invoice.tsx">
                {t('subscription:Invoice.priceInfo.totalAmountNotIncludedVat')}
              </Else>
            </If>
          </dt>
          <dd>
            <If condition={paymentStatus === PaymentStatus.REFUNDED} data-sentry-element="If" data-sentry-source-file="Invoice.tsx">
              <Then data-sentry-element="Then" data-sentry-source-file="Invoice.tsx">
                {fCurrency(0, currency, {
                currencyString
              })}
              </Then>
              <Else data-sentry-element="Else" data-sentry-source-file="Invoice.tsx">
                {fCurrency(finalPrice, currency, {
                currencyString
              })}
              </Else>
            </If>
          </dd>
        </dl>
      </div>

      <style global jsx>{`
        body {
          background-color: ${Colors.Gray50};
        }
      `}</style>
    </div>;
};
export const Invoice = withAsyncBoundary(InvoiceContent, {
  rejectedFallback: () => <SubscriptionRejectedFallback />,
  onError: error => {
    console.log(error);
  },
  pendingFallback: <InvoicePageSkeleton />
});